<template>
  <div class="upload-artwork-wrapper w-100">
    <div class="w-100 mt-2 d-flex">
      <ButtonUpload :config="{ multiple: true, uploadFromUrl: false, group: false, uploadNow: false }" @changeValue="handleSmartUpload" class="flex-1 mr-2 upload-front-btn">
        <template v-slot:beforeUploadButton="object">
          <label :for="`upload-file-${object.uid}`" class="btn btn-outline-success m-0 w-100 h-100 d-flex align-items-center justify-content-center cursor-pointer" style="height: 38px; font-weight: normal; font-size: inherit"> Front </label>
        </template>
      </ButtonUpload>
      <ButtonUpload :config="{ multiple: false, uploadFromUrl: false, group: false }" @changeValue="handleComponentUpload($event, 'back')" class="flex-1 mr-2 upload-back-btn">
        <template v-slot:beforeUploadButton="object">
          <label :for="`upload-file-${object.uid}`" class="btn btn-outline-success m-0 w-100 h-100 d-flex align-items-center justify-content-center cursor-pointer" style="height: 38px; font-weight: normal; font-size: inherit"> Back </label>
        </template>
      </ButtonUpload>
      <ButtonUpload :config="{ multiple: false, uploadFromUrl: false, group: false }" @changeValue="handleComponentUpload($event, 'cut')" class="flex-1 mr-2 upload-cut-btn">
        <template v-slot:beforeUploadButton="object">
          <label :for="`upload-file-${object.uid}`" class="btn btn-outline-success m-0 w-100 h-100 d-flex align-items-center justify-content-center cursor-pointer" style="height: 38px; font-weight: normal; font-size: inherit"> Cut file </label>
        </template>
      </ButtonUpload>
    </div>
    <div v-if="Object.keys(value || {}).length > 0" class="w-100 mt-2">
      <label class="font-weight-bold">Artwork Previews:</label>
      <ThumbnailPreview :value="value" @changeValue="changeArtworkFiles" class="design-thumbnail mt-1" editable>
        <template v-slot:after-wrapper="{ index }">
          <span class="badge badge-info position-absolute text-uppercase" style="top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 10px; pointer-events: none; opacity: 0.9">{{ index }}</span>
        </template>
      </ThumbnailPreview>
    </div>
  </div>
</template>

<script>
import ButtonUpload from '@/components/ButtonUpload.vue'
import ThumbnailPreview from '@/components/ThumbnailPreview.vue'

export default {
  emits: ['change', 'update:modelValue'],
  components: {
    ButtonUpload,
    ThumbnailPreview
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    emit(values) {
      this.$emit('change', values)
      this.$emit('update:modelValue', values)
    },
    changeArtworkFiles(values) {
      this.emit(values)
    },
    async handleComponentUpload(e, key) {
      if (!e) return
      const uploadObject = e.files ? e : Object.values(e)[0]
      if (!uploadObject) return

      const values = { ...this.value, [key]: uploadObject }
      this.emit(values)

      const file = uploadObject.files?.[0]
      if (file && uploadObject.status === 'success' && !file._validated) {
        file._validated = true
        if (!(await this.validateFile(file, key))) {
          const updatedValues = { ...this.value }
          delete updatedValues[key]
          this.emit(updatedValues)
        }
      }
    },
    async validateFile(file, key) {
      if (file.type !== 'image/png') {
        this.$util.notify({
          text: 'Only PNG files are allowed!',
          status: 'error'
        })
        return false
      }

      const loadDimensions = (src) => {
        return new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => {
            resolve({ width: img.width, height: img.height })
          }
          img.onerror = () => {
            reject(new Error('Failed to load image'))
          }
          img.src = src
        })
      }

      try {
        const newDim = await loadDimensions(URL.createObjectURL(file))
        const comparePromises = []

        Object.entries(this.value || {}).forEach(([k, item]) => {
          if (k === key) return
          let src = null
          if (_.isString(item)) {
            src = item
          } else if (_.isObjectLike(item)) {
            if (item.files && item.files[0]) {
              src = URL.createObjectURL(item.files[0])
            } else if (item.urls && item.urls[0]) {
              src = item.urls[0]
            }
          }
          if (src) {
            comparePromises.push(loadDimensions(src).then((dim) => ({ key: k, dim })))
          }
        })

        const otherDims = await Promise.all(comparePromises)
        for (const other of otherDims) {
          if (other.dim.width !== newDim.width || other.dim.height !== newDim.height) {
            this.$util.notify({
              text: 'File dimensions do not match other files!',
              status: 'error'
            })
            return false
          }
        }
      } catch (err) {
        this.$util.notify({
          text: 'An error occurred while validating file dimensions!',
          status: 'error'
        })
        return false
      }

      return true
    },
    async processSmartLogic(files) {
      const SPLIT_HEIGHT = 3160
      const SPLIT_WIDTH = 1050
      const SPLIT_PARTS = 3
      const PART_HEIGHT = SPLIT_HEIGHT / SPLIT_PARTS

      let results = { front: null, back: null, cut: null }

      const checkBlackAndTransparent = (file) => {
        return new Promise((resolve) => {
          const img = new Image()
          img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)
            const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data
            let isCut = true
            for (let i = 0; i < data.length; i += 4) {
              if (data[i + 3] > 0 && (data[i] > 15 || data[i + 1] > 15 || data[i + 2] > 15)) {
                isCut = false
                break
              }
            }
            resolve(isCut)
          }
          img.onerror = () => resolve(false)
          img.src = URL.createObjectURL(file)
        })
      }

      const splitImage = (file) => {
        return new Promise((resolve) => {
          const img = new Image()
          img.onload = async () => {
            if (img.width === SPLIT_WIDTH && img.height === SPLIT_HEIGHT) {
              const parts = await Promise.all(
                Array.from({ length: SPLIT_PARTS }, (_, i) => i).map((i) => {
                  return new Promise((resolveBlob) => {
                    const canvas = document.createElement('canvas')
                    canvas.width = SPLIT_WIDTH
                    canvas.height = PART_HEIGHT
                    const ctx = canvas.getContext('2d')
                    ctx.drawImage(img, 0, i * PART_HEIGHT, SPLIT_WIDTH, PART_HEIGHT, 0, 0, SPLIT_WIDTH, PART_HEIGHT)
                    canvas.toBlob((blob) => {
                      resolveBlob(new File([blob], `${file.name.split('.')[0]}_part${i + 1}.png`, { type: 'image/png' }))
                    }, 'image/png')
                  })
                })
              )
              resolve(parts)
            } else {
              resolve(null)
            }
          }
          img.onerror = () => resolve(null)
          img.src = URL.createObjectURL(file)
        })
      }

      if (files.length === 1) {
        const file = files[0]
        const parts = await splitImage(file)
        if (parts) {
          results.front = parts[0]
          results.back = parts[1]
          results.cut = parts[2]
        } else {
          results.front = file
        }
      } else if (files.length === 2) {
        const isCut0 = await checkBlackAndTransparent(files[0])
        const isCut1 = await checkBlackAndTransparent(files[1])
        if (isCut0 && !isCut1) {
          results.cut = files[0]
          results.front = files[1]
        } else if (isCut1 && !isCut0) {
          results.cut = files[1]
          results.front = files[0]
        } else {
          results.front = files[0]
        }
      } else {
        if (files.length > 0) results.front = files[0]
      }

      return results
    },
    async handleSmartUpload(values) {
      if (!values) return

      let files = []
      Object.values(values).forEach((val) => {
        if (val.files && val.files.length > 0) files.push(...val.files)
      })
      if (files.length === 0) return

      const assignedFiles = await this.processSmartLogic(files)

      let uploads = {}
      let currentValues = _.cloneDeep(this.value || {})

      Object.entries(assignedFiles).forEach(([key, file]) => {
        if (!file) return
        const hash = this.$util.randStr(10)
        uploads[hash] = {
          id: hash,
          title: file.name.split('.')[0],
          files: [file],
          status: 'doing',
          uploaded: 0
        }
        currentValues[key] = uploads[hash]
      })

      this.emit(currentValues)

      if (Object.keys(uploads).length > 0) {
        this.$util.storage.uploads(uploads, {
          process: (uploaded, item) => {
            let cv = _.cloneDeep(this.value || {})
            Object.keys(cv).forEach((k) => {
              if (cv[k] && cv[k].id === item.id) cv[k].uploaded = uploaded
            })
            this.emit(cv)
          },
          completed: async (response) => {
            let cv = _.cloneDeep(this.value || {})
            Object.entries(response.success).forEach(([id, item]) => {
              Object.keys(cv).forEach((k) => {
                if (cv[k] && cv[k].id === id) {
                  cv[k].fileids = { ...item.fileids }
                  cv[k].uploaded = 100
                  cv[k].status = 'success'
                }
              })
            })
            this.emit(cv)

            for (const [id] of Object.entries(response.success)) {
              let updatedCv = _.cloneDeep(this.value || {})
              for (const [k, v] of Object.entries(updatedCv)) {
                if (v && v.id === id && v.files && v.files.length > 0 && !v.files[0]._validated) {
                  v.files[0]._validated = true
                  const isValid = await this.validateFile(v.files[0], k)
                  if (!isValid) {
                    let delCv = _.cloneDeep(this.value || {})
                    delete delCv[k]
                    this.emit(delCv)
                  }
                }
              }
            }
          },
          error: (response) => {
            let cv = _.cloneDeep(this.value || {})
            Object.entries(response.error).forEach(([id]) => {
              Object.keys(cv).forEach((k) => {
                if (cv[k] && cv[k].id === id) {
                  cv[k].status = 'error'
                }
              })
            })
            this.emit(cv)
          }
        })
      }
    }
  }
}
</script>
